<!-- frontend/src/components/LCA/Stepper/LifecycleStageSelector.vue -->
<template>
    <div class="step-content">
      <StepperHeading :title="'Select Lifecycle Stage'" :step="1">
        <template #help>
          <p>
            In this step, you select the lifecycle stage during which your surplus occurs.
            This helps refine the environmental impact analysis of your surplus.
          </p>
          <p><strong>Lifecycle Stages:</strong></p>
          <ul>
            <li><strong>Agriculture:</strong> The stage where crops are cultivated and harvested.</li>
            <li><strong>Processing:</strong> Includes food processing activities like cleaning, sorting, and packaging.</li>
            <li><strong>Wholesale:</strong> Distribution of food products in large quantities, often to retailers.</li>
            <li><strong>Retail:</strong> The stage where food products are sold to consumers, such as in supermarkets.</li>
            <li><strong>Gastronomy:</strong> Food preparation and consumption in restaurants, catering, and other food service establishments.</li>
          </ul>
        </template>
      </StepperHeading>
  
      <!-- Food Surplus Info Section -->
      <div class="card mt-4 surplus-card">
        <div class="card-header bg-custom-green text-white">
          <h5 class="mb-0">Selected Food Surplus</h5>
        </div>
        <div class="card-body">
          <div class="row mb-3">
            <label class="col-sm-3 fw-bold">Food Groups:</label>
            <div class="col-sm-9">{{ foodGroupsText }}</div>
          </div>
          <div class="row mb-3">
            <label class="col-sm-3 fw-bold">Food Subgroups:</label>
            <div class="col-sm-9">{{ foodSubgroupsText }}</div>
          </div>
          <div class="row mb-3">
            <label class="col-sm-3 fw-bold">Surplus Name:</label>
            <div class="col-sm-9">{{ wasteInfo.surplusName || 'n/a' }}</div>
          </div>
          <div class="row mb-3">
            <label class="col-sm-3 fw-bold">Quantity:</label>
            <div class="col-sm-9">
              <span v-if="hasAmount">
                {{ wasteInfo.amount.value }} {{ wasteInfo.amount.unit }}
              </span>
              <span v-else>n/a</span>
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-sm-3 fw-bold">Frequency:</label>
            <div class="col-sm-9">
              <span v-if="hasFrequency">{{ frequencyString }}</span>
              <span v-else>n/a</span>
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 fw-bold">LCI Data Available:</label>
            <div class="col-sm-9">
              <span v-if="lcaData.productDetails">
                {{ lcaData.productDetails.has_lci_data ? 'Yes' : 'No' }}
              </span>
              <span v-else>{{ productNotFound ? 'No' : 'No' }}</span>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Lifecycle Stage Selection -->
      <div class="mt-4">
        <label class="form-label">Lifecycle Stage</label>
        <select class="form-select" v-model="lcaData.lifecycleStage">
          <option value="">Select lifecycle stage</option>
          <option v-for="stage in lifecycleStages" :key="stage.value" :value="stage.value">
            {{ stage.label }}
          </option>
        </select>
      </div>
  
      <!-- Explanation of Selected Lifecycle Stage -->
      <div v-if="selectedLifecycleExplanation" class="mt-3">
        <div class="card card-body info-card">
          <p>{{ selectedLifecycleExplanation }}</p>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { onMounted, computed } from 'vue';
  import StepperHeading from '@/components/Stepper/StepperHeading.vue';
  import { useStepperStore } from '@/stores/stepper';
  import { useLCAStore } from '@/stores/lca';
  import { usePageContextStore } from '@/stores/pageContext';
  
  export default {
    name: 'LifecycleStageSelector',
    components: { StepperHeading },
    setup() {
      const stepperStore = useStepperStore();
      const lcaStore = useLCAStore();
      const pageContextStore = usePageContextStore();
  
      // New Lifecycle Stages
      const lifecycleStages = [
        { value: 'Agriculture', label: 'Agriculture' },
        { value: 'Processing', label: 'Processing' },
        { value: 'Wholesale', label: 'Wholesale' },
        { value: 'Retail', label: 'Retail' },
        { value: 'Gastronomy', label: 'Gastronomy' },
      ];
  
      const lifecycleStageExplanations = {
        Agriculture: 'The Agriculture stage involves the cultivation and harvesting of crops.',
        Processing: 'The Processing stage includes activities like cleaning, sorting, and packaging of food products.',
        Wholesale: 'The Wholesale stage involves the distribution of food products in large quantities.',
        Retail: 'The Retail stage is where food products are sold to consumers, such as in supermarkets.',
        Gastronomy: 'The Gastronomy stage includes restaurants and other food service establishments.',
      };
  
      const selectedLifecycleExplanation = computed(() => {
        return lifecycleStageExplanations[lcaStore.lcaData.lifecycleStage] || '';
      });
  
      // Fetch product data if available
      onMounted(async () => {
        if (stepperStore.wasteInfo.surplusName) {
          await lcaStore.fetchProductByName(stepperStore.wasteInfo.surplusName);
        }
        pageContextStore.setPageContext(
          'Select Lifecycle Stage',
          'On this page, the user determines in which stage the surplus occurs. Note: Monthly capacities are not used in the analysis.',
          'We show the selected surplus info and if LCI data are available.'
        );
      });
  
      const wasteInfo = stepperStore.wasteInfo;
  
      // Computed strings
      const foodGroupsText = computed(() =>
        wasteInfo.foodGroups.length ? wasteInfo.foodGroups.join(', ') : 'n/a'
      );
      const foodSubgroupsText = computed(() =>
        wasteInfo.foodSubgroups.length ? wasteInfo.foodSubgroups.join(', ') : 'n/a'
      );
  
      // Check for valid amount/frequency
      const hasAmount = computed(() => {
        return !!(wasteInfo.amount.value && wasteInfo.amount.unit);
      });
      const hasFrequency = computed(() => {
        return !!(wasteInfo.frequency.times && wasteInfo.frequency.interval);
      });
      const frequencyString = computed(() => {
        const { times, interval } = wasteInfo.frequency;
        if (!times) return 'n/a';
        if (interval === 'once') {
          return times > 1 ? `${times} times` : '1 time';
        }
        return `${times} times per ${interval}`;
      });
  
      return {
        lifecycleStages,
        selectedLifecycleExplanation,
        wasteInfo,
        lcaData: lcaStore.lcaData,
        productNotFound: lcaStore.productNotFound,
        foodGroupsText,
        foodSubgroupsText,
        hasAmount,
        hasFrequency,
        frequencyString,
      };
    },
  };
  </script>
  
  <style scoped>
  .step-content {
    padding: 20px;
    background-color: #f2f5e7;
  }
  
  /* Green Header */
  .bg-custom-green {
    background-color: #87b140 !important;
  }
  
  /* Card */
  .surplus-card {
    border: none;
    border-radius: 8px;
  }
  .card-header {
    font-size: 1.25rem;
    font-weight: 600;
  }
  
  /* Info-Card (Explanation) */
  .info-card {
    background-color: #f8f9fa;
    border-left: 3px solid #87b140;
  }
  
  /* Dropdown-Item Hover */
  .dropdown-item:hover {
    background-color: #87b140 !important;
    color: #fff !important;
  }
  </style>